import { useEffect, useMemo } from 'react';

import type { IDailyStats } from '@feathr/blackbox';
import type { TTimeFrameValue } from '@feathr/components';
import { useId } from '@feathr/hooks';

import { useStore } from '../state';

interface IUseDashboardStats {
  timeFrameValue: TTimeFrameValue;
  prioritizeVerifiedActivity?: boolean;
}

interface IUseDashboardStatsReturn {
  isLoading: boolean;
  chartData: IDailyStats[];
}

function useDashboardStats({
  timeFrameValue,
  prioritizeVerifiedActivity = true,
}: IUseDashboardStats): IUseDashboardStatsReturn {
  const { Stats } = useStore();
  const listId = useId({ ephemeral: true });

  const stats = useMemo(
    () =>
      Stats.list(
        {
          model: 'account',
          filters: {
            metadata__date__gte: timeFrameValue[0],
            metadata__date__lte: timeFrameValue[1],
          },
        },
        {},
        listId,
      ),
    [Stats, timeFrameValue],
  );

  const { isPending, pagination, models } = stats;
  const hasMorePages = pagination.page < pagination.pages;

  // Automatically fetch all pages
  useEffect(() => {
    if (!isPending && hasMorePages) {
      stats.fetchMore();
    }
  }, [isPending, pagination.page, pagination.pages]);

  const chartData = useMemo(() => {
    const rawData = models.map((model) => model.toJS());

    if (!prioritizeVerifiedActivity) {
      return rawData.map((data) => ({ ...data, ...data.unfiltered }));
    }

    return rawData;
  }, [models, prioritizeVerifiedActivity]);

  return {
    isLoading: isPending && hasMorePages,
    chartData,
  };
}

export default useDashboardStats;
